import posthog from "posthog-js";

import { ENV } from "../../env";

let initialized = false;

/**
 * Core product events. Keep names stable: PostHog funnels and dashboards key
 * on them. Properties must never carry tenant or utility account data.
 */
export type AnalyticsEvent =
  | "onboarding_started"
  | "property_added"
  | "utility_connected"
  | "dashboard_viewed"
  | "incentive_match_viewed"
  | "report_exported";

export interface AnalyticsUserContext {
  /** ZEOS customer (participant org) -- the PostHog group key. */
  customerId?: number;
  customerName?: string;
  isSuperAdmin?: boolean;
}

const parseSampleRate = (raw: string | undefined): number => {
  const rate = Number(raw);
  return Number.isFinite(rate) ? Math.min(Math.max(rate, 0), 1) : 0;
};

/**
 * PostHog is opt-in per environment: with no VITE_POSTHOG_KEY every call below
 * is a no-op, so loc/test builds never send events.
 */
export const Analytics = {
  init: () => {
    if (initialized || !ENV.POSTHOG_KEY) {
      return;
    }
    // Replay is off unless a sample rate > 0 is configured; sampling is decided
    // once per page load.
    const replayEnabled =
      Math.random() < parseSampleRate(ENV.POSTHOG_REPLAY_SAMPLE_RATE);
    posthog.init(ENV.POSTHOG_KEY, {
      api_host: ENV.POSTHOG_HOST,
      // The router is a browser router, so history changes cover SPA pageviews.
      capture_pageview: "history_change",
      person_profiles: "identified_only",
      disable_session_recording: !replayEnabled,
      // Every input is masked; display text is masked only where an element is
      // tagged with the `ph-mask` class (utility account ids, names, emails).
      // Tag new sensitive text the same way, or it shows in recordings.
      session_recording: { maskAllInputs: true, maskTextSelector: ".ph-mask" },
      loaded: (ph) => {
        ph.register({ zeos_environment: ENV.ENVIRONMENT });
      },
    });
    initialized = true;
  },

  /** Identify by the Firebase uid only -- no email or name is sent. */
  identify: (userId: string) => {
    if (initialized) {
      posthog.identify(userId);
    }
  },

  /**
   * Attach role and the participant org once claims have loaded. Group
   * Analytics rolls metrics up by customer; users with no customer claim
   * (anonymous or mid-onboarding) are left ungrouped.
   */
  setUserContext: ({
    customerId,
    customerName,
    isSuperAdmin,
  }: AnalyticsUserContext) => {
    if (!initialized) {
      return;
    }
    posthog.setPersonProperties({
      role: isSuperAdmin ? "super_admin" : "user",
    });
    if (customerId) {
      posthog.group("customer", String(customerId), {
        name: customerName,
      });
    }
  },

  /** Call on sign-out so the next user does not inherit this identity. */
  reset: () => {
    if (initialized) {
      posthog.reset();
    }
  },

  capture: (event: AnalyticsEvent, properties?: Record<string, unknown>) => {
    if (initialized) {
      posthog.capture(event, properties);
    }
  },
};
